<template>
  <CustomModal
    :id="`item-task-changed-${comment.id}`"
    modalClass="custom-modal_item-task"
    width="75%"
  >
    <template #header>
      <div class="item-content-changed__header">
        <CommentOwner
          :user="comment.owner"
          userNameWeight="400"
        >
          <template #username="{ userName }">
            {{ userName }} <span class="heading-level-3">updated Task</span>
          </template>
        </CommentOwner>
        <span class="body-text-large-400">{{ comment.timeAndDateReversed }}</span>
      </div>
    </template>
    <template #body>
      <span class="item-content-changed__content">
        <span class="item-content-changed__label">Before:</span>
        <TaskItem
          :task="beforeTask"
          :isOpen="true"
          :availableTaskTypes="availableTaskTypes"
          class="pointer-none"
        />
        <div class="item-content-changed__description">
          <Editor
            id="beforeTask"
            type="read"
            :value="beforeTask.structuredDescription?.html ?? ''"
            :targetConfig="targetConfig"
            :referenceFileTargetConfig="targetConfig"
            :extensions="extensions"
          />
        </div>
        <template v-if="hasSpec">
          <span class="item-content-changed__label">Spec:</span>
          <div class="item-content-changed__description">
            <Editor
              id="beforeTaskSpec"
              type="read"
              :value="beforeTask.spec?.html ?? ''"
              :targetConfig="targetConfig"
              :referenceFileTargetConfig="targetConfig"
              :extensions="extensions"
            />
          </div>
        </template>
      </span>
      <span class="item-content-changed__content">
        <span class="item-content-changed__label">After:</span>
        <TaskItem
          :task="afterTask"
          :isOpen="true"
          class="pointer-none"
        />
        <div class="item-content-changed__description">
          <Editor
            id="afterTask"
            type="read"
            :value="afterTask.structuredDescription?.html ?? ''"
            :targetConfig="targetConfig"
            :referenceFileTargetConfig="targetConfig"
            :extensions="extensions"
          />
        </div>
        <template v-if="hasSpec">
          <span class="item-content-changed__label">Spec:</span>
          <div class="item-content-changed__description">
            <Editor
              id="afterTaskSpec"
              type="read"
              :value="afterTask.spec?.html ?? ''"
              :targetConfig="targetConfig"
              :referenceFileTargetConfig="targetConfig"
              :extensions="extensions"
            />
          </div>
        </template>
      </span>
    </template>

    <template #footer="{ hide }">
      <ControlButtons
        :hideCancel="true"
        @save="hide"
      >
        <template #save> Ok </template>
      </ControlButtons>
    </template>
  </CustomModal>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Prop } from "vue-property-decorator"
import TaskItemExtension from "@tiptap/extension-task-item"
import TaskListExtension from "@tiptap/extension-task-list"
import { Action, Getter } from "s-vuex-class"

import ControlButtons from "@/components/ControlButtons/index.vue"
import Editor from "@/components/Editor/index.vue"
import CustomModal from "@/components/Modal/index.vue"
import TaskItem from "@/components/Task/TaskItem.vue"
import CommentOwner from "@/components/User/index.vue"

import { Comment, Item, Task, TaskTypeOption, TypeConfig, UploadAttachment } from "@/models"
import { Attachment } from "@/plugins/extensions/Attachment"
import { ReferenceAllUser, ReferenceAt } from "@/plugins/extensions/reference/referenceAt"
import {
  ReferenceHashEpic,
  ReferenceHashFile,
  ReferenceHashFileLink,
  ReferenceHashIdea,
  ReferenceHashItem,
  ReferenceHashMilestone,
  ReferenceHashProject,
  ReferenceHashSprint,
  ReferenceHashTask,
  ReferenceHashTeam,
  ReferenceHashTip
} from "@/plugins/extensions/reference/referenceHash"
import { ReferenceLink } from "@/plugins/extensions/reference/referenceLink"
import { Actions, Getters } from "@/store"

@Options({
  name: "ModalItemTaskChanged",
  components: {
    CustomModal,
    ControlButtons,
    CommentOwner,
    Editor,
    TaskItem
  }
})
export default class extends Vue {
  @Prop({ required: true }) comment: Comment

  @Getter(Getters.GET_ITEM) item: Item
  @Getter(Getters.GET_TASKS_WITH_EMPTY_TASK) tasks: Task[]
  @Getter(Getters.AVAILABLE_TASK_TYPES) availableTaskTypes: TaskTypeOption[]
  @Action(Actions.ATTACHMENT_UPLOAD) attachmentUpload: (payload: UploadAttachment) => Promise<any>

  get extensions() {
    const extensions = [
      Attachment(this.attachmentUpload as any, this.targetConfig as TypeConfig | null, true),
      ReferenceAt,
      ReferenceHashProject,
      ReferenceHashMilestone,
      ReferenceHashEpic,
      ReferenceHashIdea,
      ReferenceHashTeam,
      ReferenceHashSprint,
      ReferenceHashItem,
      ReferenceHashTask,
      ReferenceHashTip,
      ReferenceHashFile,
      ReferenceHashFileLink,
      ReferenceLink,
      TaskListExtension.configure({
        HTMLAttributes: {
          class: "custom-check-list"
        }
      }),
      TaskItemExtension.configure({
        nested: true,
        HTMLAttributes: {
          class: "custom-check-list__item"
        }
      })
    ]
    if (this.item?.id) {
      extensions.push(ReferenceAllUser)
    }
    return extensions
  }

  get beforeTask() {
    return Task.create(this.comment.itemAuditRecord?.taskBefore ?? undefined)
  }

  get afterTask() {
    return Task.create(this.comment.itemAuditRecord?.taskAfter ?? undefined)
  }

  get hasSpec() {
    return !!(this.beforeTask.spec?.html || this.afterTask.spec?.html)
  }

  get targetConfig() {
    const taskId =
      this.comment.itemAuditRecord?.taskAfter?.id ?? this.comment.itemAuditRecord?.taskBefore?.id

    if (taskId) {
      return {
        type: "TASK",
        targetId: taskId
      }
    }

    if (this.item?.id) {
      return {
        type: "ITEM",
        targetId: this.item.id
      }
    }

    return null
  }
}
</script>
